import React from "react"
import JSZip from "jszip"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as sessions from "sessions"
import * as api from "brandguard/api"
import * as cache from "media/cache"
import * as media from "media"
import * as bglayouts from "brandguard/components/layouts"

interface props {
	uploadItems: api.VideoSearchResponseItem[]
	historicalItems: api.VideoSearchResponseItem[]
	deleteAction?(
		bid: string,
		id: string,
		req: api.VideoPatchRequest,
		...options: httpx.option[]
	): Promise<api.VideoPatchResponse>
	onDelete?(items: api.VideoSearchResponseItem[]): void
}

export default function MultiActions(props: React.PropsWithChildren<props>): JSX.Element {
	const { uploadItems, historicalItems, deleteAction, onDelete, children } = props
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const bearertoken = sessions.useToken()
	const allItems = [...uploadItems, ...historicalItems]

	const handleDelete = (): void => {
		const promises: Promise<api.VideoPatchRequest>[] = []
		uploadItems.forEach((i) => {
			i.media!.tombstoned_at = new Date().toISOString()
			promises.push(deleteAction!(i.media!.brand_id, i.media!.id, i, authzc.bearer(authzaccount)))
		})
		Promise.all(promises)
			.then((results) => {
				onDelete!(results)
			})
			.catch((cause) => console.error("unable to multi-select brandguard videos", cause))
	}

	const handleDownload = (): void => {
		if (uploadItems.length === 1) {
			downloadSingle(uploadItems[0])
			return
		}

		const zip = new JSZip()

		const downloadZip = (): void => {
			zip.generateAsync({ type: "blob" }).then((content) => {
				const downloadLink = document.createElement("a")
				downloadLink.href = URL.createObjectURL(content)
				downloadLink.download = "brandguard-assets.zip"
				downloadLink.click()
			})
		}

		const promises: Promise<api.VideoPatchRequest | JSZip>[] = []
		allItems.forEach((i) => {
			promises.push(
				cache.current(authzaccount.current.id, i.media!.md5, bearertoken).then((url) => {
					return fetch(url)
						.then((response) => {
							return response.arrayBuffer()
						})
						.then((videoBuffer) => {
							const videoName = media.downloads.filename(i.media!.md5, i.media!.mimetype)
							return zip.file(videoName, videoBuffer)
						})
				}),
			)
		})

		Promise.all(promises).then(() => {
			downloadZip()
		})
	}

	const downloadSingle = (i: api.VideoSearchResponseItem): void => {
		media.downloads.file(
			i.media!.md5,
			i.media!.mimetype,
			cache.current(authzaccount.current.id, i.media!.md5, bearertoken),
		)
	}

	if (!allItems.length) return <></>

	return (
		<bglayouts.DisplayActions
			cnt={allItems.length}
			canDelete={uploadItems.length > 0 && deleteAction !== undefined && onDelete !== undefined}
			handleDelete={handleDelete}
			handleDownload={handleDownload}
		>
			{children}
		</bglayouts.DisplayActions>
	)
}

MultiActions.defaultProps = {
	uploadItems: [],
	historicalItems: [],
}
